import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, User, Edit, Trash2, Zap } from 'lucide-react'
import { api } from '../lib/api'
import { formatTimestamp } from '../lib/utils'
import type { Persona } from '../types/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'

const personaSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
  prompt: z.string().min(10, 'Prompt must be at least 10 characters').max(2000, 'Prompt must be less than 2000 characters'),
})

type PersonaFormData = z.infer<typeof personaSchema>

export function Personas() {
  const queryClient = useQueryClient()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingPersona, setEditingPersona] = useState<Persona | null>(null)

  // Fetch personas
  const { data: personasData, isLoading } = useQuery({
    queryKey: ['personas'],
    queryFn: () => api.getPersonas(),
  })

  // Create persona mutation
  const createPersonaMutation = useMutation({
    mutationFn: (data: PersonaFormData) => api.createPersona(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personas'] })
      setIsCreateDialogOpen(false)
      createForm.reset()
    },
  })

  // Update persona mutation
  const updatePersonaMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: PersonaFormData }) => 
      api.updatePersona(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personas'] })
      setEditingPersona(null)
      editForm.reset()
    },
  })

  // Delete persona mutation
  const deletePersonaMutation = useMutation({
    mutationFn: (id: number) => api.deletePersona(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personas'] })
    },
  })

  // Create form
  const createForm = useForm<PersonaFormData>({
    resolver: zodResolver(personaSchema),
    defaultValues: {
      name: '',
      prompt: '',
    },
  })

  // Edit form
  const editForm = useForm<PersonaFormData>({
    resolver: zodResolver(personaSchema),
    defaultValues: {
      name: '',
      prompt: '',
    },
  })

  const onCreateSubmit = (data: PersonaFormData) => {
    createPersonaMutation.mutate(data)
  }

  const onEditSubmit = (data: PersonaFormData) => {
    if (editingPersona) {
      updatePersonaMutation.mutate({ id: editingPersona.id, data })
    }
  }

  const handleEdit = (persona: Persona) => {
    setEditingPersona(persona)
    editForm.reset({
      name: persona.name,
      prompt: persona.prompt,
    })
  }

  const handleDelete = (persona: Persona) => {
    if (confirm(`Are you sure you want to delete "${persona.name}"? This action cannot be undone.`)) {
      deletePersonaMutation.mutate(persona.id)
    }
  }

  const personas = personasData?.personas || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-3xl font-bold">AI Personas</h1>
          </div>
          <p className="text-muted-foreground">
            Manage AI personas that define how tests behave and interact with your applications. Use these personas in browser automation tasks for consistent AI behavior.
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700">
              <Plus className="h-4 w-4 mr-2" />
              New Persona
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Create New Persona</DialogTitle>
              <DialogDescription>
                Define a new AI persona with specific behavior and characteristics for testing.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={createForm.handleSubmit(onCreateSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">Name</Label>
                <Input
                  id="create-name"
                  placeholder="e.g., Customer Service Agent"
                  {...createForm.register('name')}
                />
                {createForm.formState.errors.name && (
                  <p className="text-sm text-destructive">{createForm.formState.errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-prompt">Behavior Prompt</Label>
                <Textarea
                  id="create-prompt"
                  placeholder="Describe how this AI persona should behave during testing..."
                  rows={6}
                  {...createForm.register('prompt')}
                />
                {createForm.formState.errors.prompt && (
                  <p className="text-sm text-destructive">{createForm.formState.errors.prompt.message}</p>
                )}
                <p className="text-sm text-muted-foreground">
                  Be specific about the persona's goals, behavior, and how they should interact with the application.
                </p>
              </div>

              <div className="flex justify-end space-x-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsCreateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={createPersonaMutation.isPending}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                >
                  {createPersonaMutation.isPending ? 'Creating...' : 'Create Persona'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Personas Grid */}
      {isLoading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : personas.length === 0 ? (
        <div className="text-center py-12">
          <User className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-2 text-sm font-medium">No personas</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Get started by creating your first AI persona.
          </p>
          <div className="mt-6">
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Persona
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {personas.map((persona) => (
            <Card key={persona.id} className="relative">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{persona.name}</CardTitle>
                    <CardDescription>
                      Created {formatTimestamp(persona.created_at)}
                    </CardDescription>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <Edit className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(persona)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDelete(persona)}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-4">
                  {persona.prompt}
                </p>
                <div className="mt-4">
                  <Badge variant="secondary">
                    <Zap className="h-3 w-3 mr-1" />
                    AI Persona
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingPersona} onOpenChange={() => setEditingPersona(null)}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Persona</DialogTitle>
            <DialogDescription>
              Update the persona's behavior and characteristics.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                {...editForm.register('name')}
              />
              {editForm.formState.errors.name && (
                <p className="text-sm text-destructive">{editForm.formState.errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-prompt">Behavior Prompt</Label>
              <Textarea
                id="edit-prompt"
                rows={6}
                {...editForm.register('prompt')}
              />
              {editForm.formState.errors.prompt && (
                <p className="text-sm text-destructive">{editForm.formState.errors.prompt.message}</p>
              )}
            </div>

            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setEditingPersona(null)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={updatePersonaMutation.isPending}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              >
                {updatePersonaMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
